/**
 * CareLogix™ ORACLE — External Delivery Approval™ (Phase 44, AdminOne panel).
 * CEO-controlled, role-gated, readiness-checked, EvidenceOS-audited approval to
 * move an individual AI Business service from INTERNAL_BETA to PRODUCTION.
 */
import React, { useCallback, useEffect, useState } from "react";
import { deliveryApprovalApi } from "@/api/deliveryApprovalClient";

const C = {
  panel: "rgba(13,26,15,0.85)", ivory: "#f5f5dc", ivoryD: "#d4d4aa",
  gold: "#facc15", emerald: "#7AC860", amber: "#fcd34d", red: "#ef4444",
  border: "rgba(245,200,66,0.30)",
};
const S = {
  wrap: { background: "#0a0f0a", color: C.ivory, padding: "24px 22px", fontFamily: "'Inter', system-ui, sans-serif", borderTop: `2px solid ${C.border}` },
  kicker: { color: C.gold, fontSize: 11, letterSpacing: "0.22em", fontWeight: 800, textTransform: "uppercase" },
  title: { fontFamily: "'Cinzel', serif", color: C.gold, fontSize: 24, margin: "6px 0 14px" },
  statusGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 18 },
  statCard: { background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14 },
  statNum: { fontSize: 26, color: C.gold, fontWeight: 700 },
  statLbl: { color: C.ivoryD, fontSize: 12 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", color: C.gold, borderBottom: `1px solid ${C.border}`, padding: "8px 10px", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.06em" },
  td: { borderBottom: "1px solid rgba(245,200,66,0.12)", padding: "10px", verticalAlign: "top", color: C.ivoryD },
  btn: { background: C.gold, color: "#0a0a0a", border: "none", padding: "7px 12px", borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 12, minHeight: 40, marginRight: 6, marginTop: 4 },
  btnGhost: { background: "transparent", color: C.ivory, border: `1px solid ${C.border}` },
  btnDanger: { background: "transparent", color: C.red, border: `1px solid ${C.red}` },
  badge: (bg, fg) => ({ display: "inline-block", padding: "3px 9px", borderRadius: 999, fontSize: 11, fontWeight: 700, background: bg, color: fg, border: `1px solid ${fg}` }),
  modalWrap: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 16 },
  modal: { background: "#0d1a0f", border: `1px solid ${C.gold}`, borderRadius: 16, padding: 24, maxWidth: 560, width: "100%", maxHeight: "88vh", overflowY: "auto" },
  input: { width: "100%", padding: "10px 12px", borderRadius: 8, background: "rgba(0,0,0,0.4)", color: C.ivory, border: `1px solid ${C.border}`, fontSize: 14, marginTop: 6 },
  err: { color: C.red, fontSize: 12.5, marginTop: 10 },
  note: { color: C.ivoryD, fontSize: 11.5, marginTop: 4, lineHeight: 1.5 },
};

const STATE_STYLE = {
  INTERNAL_BETA:        S.badge("rgba(212,212,170,0.12)", C.ivoryD),
  APPROVAL_PENDING:     S.badge("rgba(252,211,77,0.12)", C.amber),
  PRODUCTION_APPROVED:  S.badge("rgba(122,200,96,0.14)", C.emerald),
  PRODUCTION_SUSPENDED: S.badge("rgba(239,68,68,0.12)", C.red),
  PRODUCTION_REVOKED:   S.badge("rgba(239,68,68,0.18)", C.red),
};

function errText(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Request failed";
  if (typeof d === "string") return d;
  if (d.reason) return d.unmet_requirements ? `${d.reason} — Unmet: ${d.unmet_requirements.join(", ")}` : d.reason;
  return JSON.stringify(d);
}

export const ExternalDeliveryApprovalPanel = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {action, service}
  const [rationale, setRationale] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [totp, setTotp] = useState("");
  const [readiness, setReadiness] = useState(null);
  const [busy, setBusy] = useState(false);
  const [evidence, setEvidence] = useState(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const [l, s] = await Promise.all([deliveryApprovalApi.list(), deliveryApprovalApi.summary()]);
      setRows(l.data.services || []);
      setSummary(s.data);
    } catch (e) { setErr(errText(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openModal = async (action, service) => {
    setModal({ action, service }); setRationale(""); setConfirm(false); setTotp(""); setReadiness(null); setEvidence(null); setErr(null);
    if (action === "approve") {
      try { const r = await deliveryApprovalApi.readiness(service.service_id); setReadiness(r.data); } catch (e) { setErr(errText(e)); }
    }
    if (action === "evidence") {
      try { const r = await deliveryApprovalApi.evidence(service.service_id); setEvidence(r.data.evidence_records || []); } catch (e) { setErr(errText(e)); }
    }
  };
  const closeModal = () => { setModal(null); setErr(null); };

  const submit = async () => {
    if (!modal) return;
    setBusy(true); setErr(null);
    const id = modal.service.service_id;
    try {
      if (modal.action === "request") await deliveryApprovalApi.request(id, rationale);
      else if (modal.action === "approve") await deliveryApprovalApi.approve(id, { approval_rationale: rationale, confirm, totp_code: totp || undefined });
      else if (modal.action === "suspend") await deliveryApprovalApi.suspend(id, { reason: rationale, totp_code: totp || undefined });
      else if (modal.action === "revoke") await deliveryApprovalApi.revoke(id, { reason: rationale, totp_code: totp || undefined });
      await load(); closeModal();
    } catch (e) { setErr(errText(e)); }
    finally { setBusy(false); }
  };

  return (
    <section style={S.wrap} data-testid="external-delivery-approval-panel">
      <div style={S.kicker}>ORACLE · CEO CONTROL</div>
      <h2 style={S.title}>External Delivery Approval™</h2>

      {summary && (
        <div style={S.statusGrid} data-testid="eda-status-card">
          <div style={S.statCard}><div style={S.statLbl}>External Delivery Status</div><div style={{ ...S.statNum, fontSize: 16 }} data-testid="eda-master-mode">{summary.external_delivery_status}</div><div style={S.note}>Env gate: {summary.external_env_enabled ? "ENABLED" : "DISABLED"}</div></div>
          <div style={S.statCard}><div style={S.statLbl}>Approved Services</div><div style={S.statNum} data-testid="eda-approved-count">{summary.approved_services} / {summary.total_services}</div></div>
          <div style={S.statCard}><div style={S.statLbl}>Pending Approval</div><div style={S.statNum} data-testid="eda-pending-count">{summary.pending_approval}</div></div>
          <div style={S.statCard}><div style={S.statLbl}>Suspended</div><div style={S.statNum}>{summary.suspended}</div></div>
          <div style={S.statCard}><div style={S.statLbl}>Critical Risk Blocks</div><div style={S.statNum}>{summary.critical_risk_blocks}</div></div>
        </div>
      )}
      {summary?.latest_decision && (
        <p style={S.note} data-testid="eda-latest-decision">Latest decision: <strong style={{ color: C.ivory }}>{summary.latest_decision.service_name}</strong> · {summary.latest_decision.decision} · {summary.latest_decision.actor} · {summary.latest_decision.at?.slice(0, 19)?.replace("T", " ")}</p>
      )}
      {err && !modal && <p style={S.err} data-testid="eda-error">{err}</p>}
      {loading && <p style={S.note}>Loading approvals…</p>}

      <div style={{ overflowX: "auto" }}>
        <table style={S.table} data-testid="eda-table">
          <thead>
            <tr>
              <th style={S.th}>Service</th><th style={S.th}>Category</th><th style={S.th}>State</th>
              <th style={S.th}>Human Review</th><th style={S.th}>Compliance Risk</th>
              <th style={S.th}>Approved By</th><th style={S.th}>External Delivery</th><th style={S.th}>Controls</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.service_id} data-testid={`eda-row-${s.service_id}`}>
                <td style={{ ...S.td, color: C.ivory }}>{s.service_name}</td>
                <td style={S.td}>{s.category}</td>
                <td style={S.td}><span style={STATE_STYLE[s.delivery_state] || STATE_STYLE.INTERNAL_BETA} data-testid={`eda-state-${s.service_id}`}>{s.delivery_state}</span></td>
                <td style={S.td}>{s.human_review_required ? "Required" : "—"}</td>
                <td style={S.td}>{s.compliance_risk}</td>
                <td style={S.td}>{s.external_delivery_approved_by || "—"}<br /><span style={{ fontSize: 11 }}>{s.external_delivery_approved_at?.slice(0, 10) || ""}</span></td>
                <td style={S.td}>{s.external_delivery_status}</td>
                <td style={S.td}>
                  {(s.delivery_state === "INTERNAL_BETA" || s.delivery_state === "PRODUCTION_REVOKED") && (
                    <button style={{ ...S.btn, ...S.btnGhost }} onClick={() => openModal("request", s)} data-testid={`eda-request-${s.service_id}`} type="button">Request Production Approval</button>
                  )}
                  {(s.delivery_state === "APPROVAL_PENDING" || s.delivery_state === "PRODUCTION_SUSPENDED") && (
                    <button style={S.btn} onClick={() => openModal("approve", s)} data-testid={`eda-approve-${s.service_id}`} type="button">Approve for Production</button>
                  )}
                  {s.delivery_state === "PRODUCTION_APPROVED" && (
                    <>
                      <button style={{ ...S.btn, ...S.btnDanger }} onClick={() => openModal("suspend", s)} data-testid={`eda-suspend-${s.service_id}`} type="button">Suspend External Delivery</button>
                      <button style={{ ...S.btn, ...S.btnDanger }} onClick={() => openModal("revoke", s)} data-testid={`eda-revoke-${s.service_id}`} type="button">Revoke Production Approval</button>
                    </>
                  )}
                  <button style={{ ...S.btn, ...S.btnGhost }} onClick={() => openModal("evidence", s)} data-testid={`eda-evidence-${s.service_id}`} type="button">View Evidence Record</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div style={S.modalWrap} role="dialog" aria-modal="true" aria-label="Delivery approval action" data-testid="eda-modal">
          <div style={S.modal}>
            <div style={S.kicker}>{modal.action.toUpperCase()}</div>
            <h3 style={{ ...S.title, fontSize: 19, margin: "6px 0 10px" }}>{modal.service.service_name}</h3>

            {modal.action === "evidence" ? (
              <div data-testid="eda-evidence-list">
                {(!evidence || evidence.length === 0) && <p style={S.note}>No decision records yet.</p>}
                {(evidence || []).map((rec) => (
                  <div key={rec.approval_id} style={{ ...S.statCard, marginBottom: 8 }}>
                    <div style={{ color: C.gold, fontWeight: 700, fontSize: 13 }}>{rec.decision} · {rec.status}</div>
                    <div style={S.note}>{rec.previous_state} → {rec.new_state}</div>
                    <div style={S.note}>By {rec.actor_id} ({rec.actor_role}) · {rec.approved_at?.slice(0, 19)?.replace("T", " ")}</div>
                    {rec.step_up_method && <div style={S.note}>Step-up: {rec.step_up_method}</div>}
                    {rec.evidence_hash && <div style={{ ...S.note, wordBreak: "break-all" }}>SHA-256: {rec.evidence_hash}</div>}
                    {rec.approval_rationale && <div style={S.note}>Rationale: {rec.approval_rationale}</div>}
                  </div>
                ))}
              </div>
            ) : (
              <>
                {modal.action === "approve" && readiness && (
                  <div style={{ ...S.statCard, marginBottom: 12 }} data-testid="eda-readiness">
                    <div style={{ color: readiness.ready ? C.emerald : C.red, fontWeight: 700, fontSize: 13 }}>
                      Readiness: {readiness.ready ? "READY" : "BLOCKED"}
                    </div>
                    <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 12, color: C.ivoryD, lineHeight: 1.6 }}>
                      {Object.entries(readiness.checks).map(([k, v]) => {
                        const good = k === "critical_risks_open" ? v === false : v === true;
                        return <li key={k} style={{ color: good ? C.emerald : C.red }}>{good ? "✓" : "✕"} {k}</li>;
                      })}
                    </ul>
                    <p style={S.note}>Env gate: {readiness.external_env_enabled ? "ENABLED" : "DISABLED (production activation authorizes the service; external send remains blocked until env gate is enabled at deploy)"}.</p>
                  </div>
                )}

                <label htmlFor="eda-rationale" style={{ ...S.statLbl, display: "block" }}>
                  {modal.action === "approve" ? "Written approval rationale (required)" : modal.action === "request" ? "Request notes" : "Reason (required)"}
                  <textarea id="eda-rationale" style={{ ...S.input, minHeight: 84 }} value={rationale}
                    onChange={(e) => setRationale(e.target.value)} data-testid="eda-rationale-input" />
                </label>

                {(modal.action === "approve") && (
                  <>
                    <label style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 12, fontSize: 12.5, color: C.ivoryD }}>
                      <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} data-testid="eda-confirm-checkbox" style={{ marginTop: 3 }} />
                      <span>I confirm this service meets CareLogix human-quality-control standards and authorize external client delivery. Human review remains mandatory.</span>
                    </label>
                    <label htmlFor="eda-totp" style={{ ...S.statLbl, display: "block", marginTop: 10 }}>
                      AdminOne 2FA code (if enrolled)
                      <input id="eda-totp" style={S.input} value={totp} inputMode="numeric" autoComplete="one-time-code"
                        onChange={(e) => setTotp(e.target.value)} data-testid="eda-totp-input" placeholder="6-digit code" />
                    </label>
                  </>
                )}
                {(modal.action === "suspend" || modal.action === "revoke") && (
                  <label htmlFor="eda-totp2" style={{ ...S.statLbl, display: "block", marginTop: 10 }}>
                    AdminOne 2FA code (if enrolled)
                    <input id="eda-totp2" style={S.input} value={totp} inputMode="numeric"
                      onChange={(e) => setTotp(e.target.value)} data-testid="eda-totp-input" placeholder="6-digit code" />
                  </label>
                )}
              </>
            )}

            {err && <p style={S.err} data-testid="eda-modal-error">{err}</p>}
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              {modal.action !== "evidence" && (
                <button style={S.btn} onClick={submit} disabled={busy || (modal.action === "approve" && !readiness?.ready)}
                  data-testid="eda-modal-submit" type="button">
                  {busy ? "Working…" : modal.action === "approve" ? "Activate Production" : modal.action === "request" ? "Submit Request" : "Confirm"}
                </button>
              )}
              <button style={{ ...S.btn, ...S.btnGhost }} onClick={closeModal} data-testid="eda-modal-cancel" type="button">Close</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
